import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage, apiErrorSubject } from '../lib/errors'
import { formatDateTime } from '../lib/format'
import { setActivation, type OwnerType } from '../features/alerts/api'
import { RecordLink } from './RecordLink'

/**
 * Standing a record down, and bringing it back. This is what the platform has instead
 * of delete, and it always will be — from Stage 3 on, loads and invoices reference
 * these records, so a carrier that could be erased would take a year of settled
 * paperwork with it.
 *
 * It is deliberately not a compliance chip. "We no longer work with them" is stated by
 * a person; "their insurance lapsed" is concluded by the nightly evaluation. A record
 * can be either, both, or neither, and a screen that showed one chip for the two would
 * leave a dispatcher unable to tell which of them he can do something about.
 */
export function ActivationPanel({
  ownerType,
  ownerId,
  active,
  deactivatedAt,
  onChanged,
  /** What a deactivation takes with it, when it takes anything. */
  cascadeWarningKey,
}: {
  ownerType: OwnerType
  ownerId: string
  active: boolean
  deactivatedAt: string | null
  onChanged: (active: boolean, deactivatedAt: string | null) => void
  cascadeWarningKey?: string
}) {
  const { t, i18n } = useTranslation()

  const [isSaving, setIsSaving] = useState(false)
  const [failure, setFailure] = useState<{ error: unknown } | null>(null)
  // Set when the server saved the change but could not confirm the new standing.
  // Not an error: the save happened. Shown anyway, because a badge nobody re-judged
  // looks exactly like one that was.
  const [standingUnconfirmed, setStandingUnconfirmed] = useState(false)

  async function handleToggle() {
    // Only the half that removes something from play asks. Coming back is harmless
    // and reversible; going away takes a fleet with it.
    if (active && !window.confirm(t('activation.stopConfirm'))) {
      return
    }

    setIsSaving(true)
    setFailure(null)

    try {
      const result = await setActivation(ownerType, ownerId, !active)
      setStandingUnconfirmed(!result.standingConfirmed)
      onChanged(result.active, result.deactivatedAt)
    } catch (caught) {
      setFailure({ error: caught })
    } finally {
      setIsSaving(false)
    }
  }

  const holder = failure === null ? null : apiErrorSubject(failure.error)

  return (
    <section className="fleet activation">
      <header className="page__header--row fleet__header">
        <h2>{t('activation.title')}</h2>
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void handleToggle()}
          disabled={isSaving}
        >
          {isSaving
            ? t('activation.saving')
            : active
              ? t('activation.stop')
              : t('activation.resume')}
        </button>
      </header>

      <p className="page__subtitle">
        {active
          ? t('activation.isActive')
          : t('activation.isInactive', {
              when:
                deactivatedAt === null ? '—' : formatDateTime(deactivatedAt, i18n.language),
            })}
      </p>

      {active && cascadeWarningKey !== undefined && (
        <p className="page__subtitle">{t(cascadeWarningKey)}</p>
      )}

      {standingUnconfirmed && (
        <p className="alert alert--warning" role="status">
          {/* The same sentence the documents panel says for the same condition. */}
          {t('compliance.standingUnconfirmed')}
        </p>
      )}

      {failure !== null && (
        <p className="alert" role="alert">
          {/* mc_number_taken and usdot_number_taken land here: the price of letting a
              deactivation free its numbers is that they can be gone when you come
              back — and the refusal names who holds them now (TRKC-83). */}
          {apiErrorMessage(failure.error, t, 'activation.error')}
          {holder !== null && (
            <>
              {' '}
              <RecordLink subject={holder} />
            </>
          )}
        </p>
      )}
    </section>
  )
}

/**
 * The chip for a record nobody works with any more. Its own shape and its own words,
 * never a fourth compliance state — the paperwork of a record we stopped using is
 * exactly as valid as it was the day before.
 */
export function ActivationBadge({ active }: { active: boolean }) {
  const { t } = useTranslation()

  if (active) {
    return null
  }

  return <span className="badge badge--inactive">{t('activation.inactive')}</span>
}
